/* ====================================================================
   NORGE shared design system · runtime · v1

   Defines window.NorgeDS once, guarded by ||, so however many sections
   load this file the page gets one registry, one pair of theme editor
   listeners and one copy of the delivery band table.

   Loaded with defer from snippets/norge-ds-base.liquid. A section's
   inline script may run before this file has, so the last line fires
   norge-ds:ready on the document; sections wait on that when NorgeDS is
   not yet there (see the snippet for the two line wrapper).
   ==================================================================== */
window.NorgeDS = window.NorgeDS || (function () {
  'use strict';

  var mq = function (q) {
    try { return !!(window.matchMedia && window.matchMedia(q).matches); } catch (err) { return false; }
  };
  /* read once per load: a person who changes this setting reloads */
  var reduce = mq('(prefers-reduced-motion: reduce)');
  var coarse = mq('(pointer: coarse)');

  /* ------------------------------------------------------------------
     SECTION RUNTIME. Same semantics as window.NorgeKube: sections are
     torn down and re-rendered whenever a merchant edits them in the
     theme editor. Each section registers an init function against its
     own section id; registering again destroys the previous instance
     first, so a re-render can never leave two sets of listeners, two
     animation frames or two intervals behind.
     ------------------------------------------------------------------ */
  var reg = {};

  /* Tearing an instance down runs its recorded cleanup and forgets the
     node, but KEEPS the init factory. A theme editor re-render inserts
     new markup whose inline script may or may not execute, so the
     shopify:section:load event has to be able to revive a section on
     its own; that is only possible if the factory outlives the node. */
  function teardown(id) {
    var e = reg[id];
    if (!e) return;
    if (e.destroy) { try { e.destroy(); } catch (err) {} }
    e.destroy = null;
    e.node = null;
  }

  function run(id) {
    var e = reg[id];
    if (!e) return;
    var node = document.getElementById(e.rootId);
    if (!node) { teardown(id); return; }
    if (e.node === node && e.destroy) return;   /* already live on this node */
    teardown(id);
    e.node = node;
    node.classList.remove('nds-no-js');
    var d = null;
    try { d = e.init(node); } catch (err) { if (window.console) console.error(err); }
    e.destroy = typeof d === 'function' ? d : function () {};
  }

  function register(id, rootId, init) {
    teardown(id);
    reg[id] = { rootId: rootId, init: init, destroy: null, node: null };
    run(id);
  }

  document.addEventListener('shopify:section:load', function (e) {
    run(e.detail.sectionId);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    teardown(e.detail.sectionId);
  });

  /* bind a listener and get its removal recorded, so one call tears the
     whole instance down */
  function binder() {
    var undo = [];
    return {
      on: function (el, ev, fn, opts) {
        if (!el) return;
        el.addEventListener(ev, fn, opts);
        undo.push(function () { el.removeEventListener(ev, fn, opts); });
      },
      add: function (fn) { undo.push(fn); },
      destroy: function () {
        while (undo.length) { try { undo.pop()(); } catch (err) {} }
      }
    };
  }

  function live() {
    var out = {};
    for (var k in reg) { if (reg[k].node && reg[k].destroy) { out[k] = true; } }
    return out;
  }

  /* ------------------------------------------------------------------
     MONEY AND CONTACT. Prices are never constants here: every figure on
     the site is the live Shopify product price, GST inclusive, and a
     section hands it to the runtime through data-price-kube and
     data-price-n02 on its root (whole dollars, written by Liquid).
     ------------------------------------------------------------------ */
  function livePrices(root) {
    var el = root && root.getAttribute ? root : document.documentElement;
    var read = function (name) {
      var v = parseFloat(el.getAttribute(name));
      return v === v && v > 0 ? v : 0;
    };
    return { KUBE: read('data-price-kube'), N02: read('data-price-n02') };
  }

  /* '$' plus the rounded figure with en-AU thousands grouping */
  function money(n) {
    var r = Math.round(Number(n) || 0);
    var s = String(Math.abs(r)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (r < 0 ? '-$' : '$') + s;
  }

  var contact = {
    phone: '0435 261 143',
    tel: '+61435261143',
    email: 'aussie@thenorge.com',
    calendly: 'https://calendly.com/aussie-thenorge/30min',
    hours: 'Mon to Fri, 9 to 5 Adelaide time',
    timezone: 'Australia/Adelaide'
  };

  /* the booking link with the page and slot stamped into the UTMs */
  function calendly(page, slot) {
    var p = encodeURIComponent(page || 'site');
    var url = contact.calendly + '?utm_source=thenorge.com&utm_medium=' + p + '&utm_campaign=' + p;
    if (slot) url += '&utm_content=' + encodeURIComponent(slot);
    return url;
  }

  /* ------------------------------------------------------------------
     DELIVERY BANDS. The theme's own precomputed haversine bands from
     snippets/norge-delivery-checker.liquid, taken as they are: an RLE
     of a 10,000 character string, index = postcode 0000 to 9999,
     '0' to '4' = band, '.' = postcode not in the dataset. Each comma
     separated token is <char><run length in base36>.
     ------------------------------------------------------------------ */
  var NORGE_RLE = '.5k,31,.gn,42,.1,42,.5,46,.4,43,.5,45,.1,48,.3,43,.2,45,.5,43,.7,46,.4,42,.3,42,.j,42,.1,41,.2j,0a,.9,04,.1,0m,.1h,02,.3,06,.1,02g,.1,03,.1,01,.1,0d,21,0k,.1e,0a,.d,01,.k,01,.4,01,.9,01,.4,01,.4,01,.14,31,.3,01,.d,02,.1,09,.4,01,.4,02,.3,01,.4,01,.4,01,.4,01,.4,02,.3,01,.4,02,.3,02,.2,02,.4,01,.2,01,.1,01,.3,01,.f,01,.18,01,.4,01,.4,01,.e,01,.4,01,.4,01,.1,01,.4,01,.r,01,.4,01,.3,02,.e,01,.2,01,.1,01,.9,01,.4,01,.4,01,.4,01,.e,01,.9,01,.4,01,.e,01,.9,02,.8,01,.4,01,.9,01,.5,01,.9,01,.8,01,.9,01,.4,01,.5,01,.7,01,.5,01,.4,02,.3,01,.f,01,.8,01,.a,01,.2,02,.9,01,.2,01,.1,02,.30,03,.1,01,.1,08,.1,010,.1,01,.2,01,.1,01,41,0j,.1,0j,.1,01e,.1,0j,.1,0a,.a,0b,.2,0c,.1,0j,.f,11,01,11,.3,02,18,.1,11,.a,21,.1,21,11,26,.1,2c,.1,27,01,33,.1,21,31,21,31,2a,32,22,.1,31,22,34,49,.1,4c,.3,41,.3,42,32,.6,44,.3,43,.1,41,.4,4c,.1,44,.3,31,.4,34,41,31,42,33,41,.7,4c,.1,45,.3,31,.1,35,.2,35,23,32,21,31,24,12,23,11,.9,11,.1,11,.2,12,.1,01,.6,16,.1,11,.2,16,.2,11,22,31,41,32,22,.3,42,.1,44,.3,06,.2,05,19,23,33,43,31,42,.1,41,.3,41,.5,35,43,33,41,31,.1,36,41,33,4a,.6,47,31,46,.1,42,.1,44,.1,41,.1,42,.1,42,.1,42,.2,41,.2,41,.1,42,.i,44,.1,43,31,.1,42,31,41,31,43,.2,43,.2,43,.1,42,31,48,.5,01,.1,02,12,01,13,.1,13,05,.1,06,.2,18,.1,15,21,.2,21,35,.1,34,.2,41,31,42,32,42,.6,42,.1,4h,.1,43,.1,43,21,31,21,33,.1,31,.b,31,41,31,45,.1,48,.9,41,01,.6,49,.4,34,.2d,07,.1,01,.1,04,.1,02,.1,0h,.1,0x,.1,07,.1,02,.1,0b,.1,05,.1,01,.1,09,.1,01,.1,04,.4,010,.1,019,.1,04,.3,01,1b,01,15,.1,11,22,31,23,33,11,23,.5,23,.2,21,.5,31,.3,3l,42,31,.1,31,.2,41,32,.5,46,.3,44,.1,42,.1,43,.1,11,23,31,.2,12,21,14,04,.1,01,12,.2,11,.4,27,11,01,.1,22,.1,22,.5,22,.1,21,32,.1,32,21,32,.2,32,.1,32,.1,44,.1,42,.2,44,.4,41,.1,41,.2,44,.2,43,.2,42,.2,07,12,.1,02,.1,13,.1,11,.1,13,.1,22,.1,21,.4,11,.1,12,24,.1,33,.2,31,.2,31,.1,32,.1,31,.1,42,.1,41,.1,45,.2,41,.1,41,.1,41,.1,43,.2,43,.1,41,.2,31,.2,21,33,.1,31,12,21,.1,31,.1,31,.1,43,.1,41,.3,31,.2,41,.1,41,.1,41,.1,41,.2,41,23,.1,26,.1,38,.1,34,.1,32,.2,43,.1,44,.1,44,.2,44,.1,41,.7,22,.1,21,.1,21,.1,21,.1,39,.4,3d,.1,32,.1,41,31,.1,31,.8,16,23,.2,21,34,.1,34,.3,32,.1,31,.1,32,43,.2,42,.1,33,42,.2,42,.1,43,.1,12,21,11,21,.1,12,22,.1,23,34,.1,31,.1,32,.1,37,.2,31,.1,32,.1,31,08,11,03,11,01,11,03,.2,01,.4,01,.1,13,.1,02,11,.1,05,.1,03,.1,02,11,.1,11,01,.1,08,11,.1,15,.1,11,.1,12,24,.5,13,.1,21,.4,21,31,21,.1,31,.2,31,.2,34,21,.1,21,34,.1,31,.1,32,.3,23,.1,33,.2,31,.1,31,.1,31,.2,49,.1,31,41,.1,31,.1,41,.1,43,.4,41,03,11,.1,12,.1,16,.1,15,02,.1,12,.1,19,41,11,.3,22,.1,22,.1,25,.1,11,.1,21,33,.3,31,.3,04,13,.2,11,.2,12,41,13,.2,22,.3,0f,.2,06,.2,01,.3,04,.1,04,.d,01,.1,03,.3,03,.2,07,.1,07,.m,0j,41,05,.1,07,.h,0b,.1,03,.3,02,41,03,.3,02,.3,02,.k,01,.1,03,1l,.13,01,12,.2,11,.4,01,.4,11,.1,11,.c,02,.1,04,11,.1,15,21,.p,16,01,11,.2,21,.1,25,31,26,32,.4,24,35,.1,36,.1,31,41,.1,41,.9,24,35,.1,34,.1,46,31,47,.p,42,.5,42,.2,41,.1,42,.1,43,.1,42,.1,46,.3,49,.1,43,.1,07,11,04,11,01,12,01,13,02,.s,1b,23,11,27,11,22,11,.4,32,.i,32,.3,32,.1,31,.1,34,22,.4,32,.3,31,42,.2,41,.j,31,.4,41,.3,31,41,.1,31,.7,42,.1,42,.1,43,.1,41,.d,42,.1,41,.1,49,.1,4k,.1,44,.1,4c,.3,42,.1,42,.1,42,.14,4s,.2,43,.i,42,.1,41,.1,48,.3,41,.2,4l,.1,43,.2,41,.2w,02,.3,0l,.5,05,.1,0g,.8,0g,.4,0i,.7,0d,.1,02,.3,03,.3,04,.1,07,.1,01,.5,0p,.q,01,13,.5,01,14,.5,31,23,.7,06,12,.1,06,.4,03,14,.2,12,21,42,22,32,42,31,43,.1,31,45,.9,42,.9,21,33,.1,21,31,22,32,.8,23,.7,34,.6,37,.3,02,12,21,12,21,.2,11,.a,14,.6,21,.i,12,.8,13,24,36,.8,44,.5,41,.a,11,22,31,21,.4,12,21,.1,21,.5,34,.6,34,.1,41,.4,32,.1,31,.1,31,.5,12,.7,21,.9,21,33,.g,31,.9,11,.1,21,.1,23,.1,21,.1,21,.9,12,22,.1,21,11,21,.2,14,.g,37,41,32,.1,41,.i,44,.6,43,.7,46,.4,42,.8,42,.8,41,.9,41,.9,42,.8,41,.2,41,.1,41,.1,41,.1,42,.1,44,.4,45,.1t,02,.8,01,.s,01,.2u,01,.7,01,.1d,02,.1,0a,.1,0n,11,01,.2,13,21,.5,0p,.1,04,.1,04,.5,01,.9,0d,.8,03,11,01,11,.k,0q,14,.3,13,.o,15,.1,12,21,.2,24,.1,22,31,21,36,21,.2,32,.1,32,.2,32,.6,36,.1,41,.1,41,.1,41,.8,31,.3,31,.4,33,.1,33,.1,41,.1,41,.b,11,.1,21,.1,21,.1,22,.1,33,.1,32,42,.1,45,.1,43,.1,44,.1,44,.2,41,.1,41,.2,41,.1,41,.1,32,42,.1,45,.1,31,.1,31,.1,31,.1,32,41,31,.1,32,.1,31,.7,21,33,.3,21,34,44,.2,11,.1,11,.1,21,.1,21,.1,37,.2,33,47,31,4a,.1,41,.1,42,.1,44,.1,41,.1,41,.7,23,31,.1,34,.1,31,.1,42,.1,33,.1,41,31,.3,41,31,.1,34,.a,01,14,22,.1,22,38,41,.1,31,41,.2,41,.2,41,.1,43,.2,43,.i,01,.1,01,.1,11,.1,11,.1,11,.1,13,21,.1,22,.1,32,.r,21,.1,32,.1,32,.2,41,32,.1,41,.3,41,.2,41,.1,41,.1,42,.1,43,.2,41,.2,43,.1,41,.3,41,.1i,41,.3,41,.1,41,.2,45,.1,41,.1,41,.1,44,.1,42,.1,41,.2,41,.1,41,.6,41,.2,41,.7,41,.1,42,.3,41,.1,43,.2,41,.4,41,.r,42,01,.2,01,.5,01,.7,01,.2,01,.6,01,.2,03,.4,0e,.e,01,.6,01,.j,01,.7,05,41,02,.1,0i,.2,01,.1,0d,41,03,.3,07,41,03,.1,08,.8,0e,.4,01,.2,43,.1,49,.2,4d,.2,41,.j,46,.1h,41,.2,42,.2,42,.1,42,.i,42,.9,42,.3,41,.6,42,.6,4b,.1,46,.2,41,.i,48,.v,43,.1,44,31,49,.1,42,.1,41,.4,43,.c,43,.7,48,.2,41,.4,42,.3,43,.2,41,.4,42,.3q,45,.eq,04,41,07,.w,01,.5,01,.e,01,.2,03,.u,01,.4,01,.3,01,.8,01,.2c,01,.4x,01,.1i,01,.20,01,.7l,01,.5y,03,.2,01,.1,01,.1,02,.2,01,.1,01,.cg,01,.79,11,.7l';
  var norge_bands = null;
  function norge_decode() {
    if (norge_bands) return norge_bands;
    var out = '';
    var parts = NORGE_RLE.split(',');
    for (var i = 0; i < parts.length; i++) {
      out += new Array(parseInt(parts[i].slice(1), 36) + 1).join(parts[i].charAt(0));
    }
    norge_bands = out;
    return out;
  }
  var BAND_PRICE = [0, 650, 850, 1200, 1800];

  /* exactly four digits after stripping spaces: "0800" is a postcode,
     "800" is not, whatever parseInt would have said */
  function postcode(str) {
    var raw = String(str == null ? '' : str).replace(/\s+/g, '');
    if (!/^\d{4}$/.test(raw)) return { ok: false, reason: 'format', postcode: raw };
    var ch = norge_decode().charAt(parseInt(raw, 10));
    if (ch === '.' || ch === '') return { ok: true, band: null, price: null, postcode: raw };
    var band = parseInt(ch, 10);
    return { ok: true, band: band, price: BAND_PRICE[band], postcode: raw };
  }

  /* the canonical copy for each outcome, one place */
  function postcodeCopy(r) {
    if (!r || !r.ok) {
      return { tone: 'no', title: 'Four digits, Australian postcode.', note: '' };
    }
    if (r.band === null) {
      return { tone: 'warn', title: 'We do not have that postcode on file.',
               note: 'Send it to us and we will quote delivery in one email.' };
    }
    if (r.band === 0) {
      return { tone: 'ok', title: 'Free metro delivery and installation.',
               note: 'Within 50 km of a capital city centre. Nothing extra to arrange.' };
    }
    return { tone: 'warn', title: 'Delivery to ' + r.postcode + ': ' + money(r.price) + '.',
             note: 'Outside the free 50 km metro zone. Installation stays included. Confirmed at checkout.' };
  }

  /* ------------------------------------------------------------------
     SMALL BEHAVIOURS. Each returns a function that undoes it, so a
     section's init can hand them to its binder.
     ------------------------------------------------------------------ */
  /* copy text, show the done label, revert after 1600ms */
  function copy(btn, text, doneLabel, idleLabel) {
    var idle = idleLabel || (btn ? btn.textContent : '');
    var t = null;
    var done = function () {
      if (!btn) return;
      btn.textContent = doneLabel || 'Copied';
      clearTimeout(t);
      t = setTimeout(function () { btn.textContent = idle; }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, done);
    } else { done(); }
    return function () { clearTimeout(t); };
  }

  /* steam: spawn wisps into an .nds-fx container. One shot by default,
     cleared after 8s; loop:true keeps them rising until cleared. */
  function wisps(container, opts) {
    var clear = function () { if (container) container.innerHTML = ''; };
    if (!container || reduce) return clear;
    var o = opts || {};
    var count = o.count || 3, loop = !!o.loop;
    clear();
    for (var i = 0; i < count; i++) {
      var w = document.createElement('div');
      w.className = 'nds-wisp' + (loop ? ' nds-wisp--loop' : '');
      w.setAttribute('aria-hidden', 'true');
      if (loop) {
        var size = 120 + Math.random() * 160;
        w.style.width = size + 'px';
        w.style.height = (size * 1.3) + 'px';
        w.style.left = (5 + Math.random() * 85) + '%';
        w.style.setProperty('--d', (9 + Math.random() * 8).toFixed(1) + 's');
        w.style.setProperty('--dl', (-Math.random() * 14).toFixed(1) + 's');
        w.style.setProperty('--sw', ((Math.random() < .5 ? -1 : 1) * (15 + Math.random() * 35)).toFixed(0) + 'px');
      } else {
        w.style.left = (10 + Math.random() * 75) + '%';
        w.style.setProperty('--d', (4.5 + Math.random() * 2.5).toFixed(1) + 's');
        w.style.setProperty('--dl', (i * 0.35).toFixed(2) + 's');
        w.style.setProperty('--sw', ((Math.random() < .5 ? -1 : 1) * (10 + Math.random() * 30)).toFixed(0) + 'px');
      }
      container.appendChild(w);
    }
    var t = loop ? null : setTimeout(clear, 8000);
    return function () { clearTimeout(t); clear(); };
  }

  /* pointer sheen: --mx and --my follow the pointer as percentages.
     Nothing for coarse pointers or reduced motion. */
  function sheen(el) {
    if (!el || reduce || coarse) return function () {};
    var move = function (e) {
      var r = el.getBoundingClientRect();
      el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
      el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
    };
    el.addEventListener('pointermove', move);
    return function () { el.removeEventListener('pointermove', move); };
  }

  /* thermometer: fill is page scroll progress, marker is tempOf() 0..1 */
  function thermo(root, o) {
    o = o || {};
    var fill = o.fill || (root && root.querySelector('b'));
    var mark = o.mark || (root && root.querySelector('i'));
    var tempOf = typeof o.tempOf === 'function' ? o.tempOf : function () { return .5; };
    if (!fill && !mark) return function () {};
    var q = false;
    var paint = function () {
      q = false;
      var d = document.documentElement;
      var p = d.scrollTop / (d.scrollHeight - d.clientHeight || 1);
      if (fill) fill.style.height = (Math.max(0, Math.min(1, p)) * 100).toFixed(1) + '%';
      var t = Number(tempOf());
      if (mark && t === t) mark.style.top = 'calc(' + (Math.max(0, Math.min(1, t)) * 100).toFixed(1) + '% - 7px)';
    };
    var tick = function () { if (!q) { q = true; requestAnimationFrame(paint); } };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    paint();
    return function () {
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
    };
  }

  /* the section's mood: 'cold', 'warm' or nothing */
  function mood(root, m) {
    if (!root) return;
    root.classList.toggle('is-cold', m === 'cold');
    root.classList.toggle('is-warm', m === 'warm');
  }

  /* a tiny localStorage set behind a key, silent when storage is off */
  function memory(key) {
    var read = function () {
      try { var v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; }
      catch (err) { return []; }
    };
    return {
      get: read,
      has: function (id) { return read().indexOf(id) > -1; },
      add: function (id) {
        var v = read();
        if (v.indexOf(id) > -1) return v;
        v.push(id);
        try { localStorage.setItem(key, JSON.stringify(v)); } catch (err) {}
        return v;
      }
    };
  }

  return {
    version: '1.0.0',
    reduce: reduce,
    coarse: coarse,
    register: register,
    run: run,
    binder: binder,
    get instances() { return live(); },
    registry: reg,
    livePrices: livePrices,
    money: money,
    contact: contact,
    calendly: calendly,
    postcode: postcode,
    postcodeCopy: postcodeCopy,
    bands: BAND_PRICE,
    copy: copy,
    wisps: wisps,
    sheen: sheen,
    thermo: thermo,
    mood: mood,
    memory: memory
  };
})();

/* sections that ran before this file can now start */
try { document.dispatchEvent(new CustomEvent('norge-ds:ready', { detail: window.NorgeDS })); } catch (err) {}
